/**
 * JobMatches — Show AI skill-matched candidates per open MRF.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, Users, ChevronDown, Star } from 'lucide-react';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function JobMatches() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [scoring, setScoring] = useState(null); // { jobId, candidateId }
  const [scoreResults, setScoreResults] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    apiGet('/hr/recruitment/matches')
      .then(d => setJobs(d.jobs || []))
      .catch(() => toast.error('Failed to load matches'))
      .finally(() => setLoading(false));
  }, []);

  const scoreCandidate = async (jobId, candidateId) => {
    const key = `${jobId}__${candidateId}`;
    if (scoreResults[key]) return; // already scored
    setScoring(key);
    try {
      const r = await apiPost('/hr/recruitment/score-against-job', { job_id: jobId, candidate_id: candidateId });
      setScoreResults(prev => ({ ...prev, [key]: r.scoring }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scoring failed');
    } finally { setScoring(null); }
  };

  const GRADE_COLOR = { A: 'text-emerald-600', B: 'text-blue-600', C: 'text-amber-600', D: 'text-orange-600', F: 'text-rose-600' };
  const REC_BG = { 'Strong Hire': 'bg-emerald-100 text-emerald-700', 'Hire': 'bg-blue-100 text-blue-700', 'Maybe': 'bg-amber-100 text-amber-700', 'No': 'bg-rose-100 text-rose-700' };

  return (
    <div className="space-y-5" data-testid="job-matches">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-purple-500" />
        <h2 className="font-display font-bold text-lg">Job Matches</h2>
        <span className="text-[10px] bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-full ml-1">AI-Powered</span>
      </div>
      <p className="text-sm text-slate-500">Each open MRF is matched against your Candidate Pool by skill overlap (≥30%). Click "AI Score" to get a detailed fit assessment.</p>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading matches…
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Users size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-bold">No matches yet</p>
          <p className="text-xs mt-1">Add candidates to the Candidate Pool and create open MRFs.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map(job => (
            <div key={job.job_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpanded(expanded === job.job_id ? null : job.job_id)}
              >
                <div>
                  <p className="font-display font-bold">{job.role_title}</p>
                  <p className="text-xs text-slate-500">{job.department} · {job.matches.length} candidate match(es)</p>
                </div>
                <div className="flex items-center gap-2">
                  {job.matches.length > 0 && (
                    <span className="text-[10px] bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-full">
                      Best: {job.matches[0]?.match_score}%
                    </span>
                  )}
                  <ChevronDown size={14} className={`text-slate-400 transition-transform ${expanded === job.job_id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {expanded === job.job_id && (
                <div className="border-t border-slate-100 px-4 py-4">
                  {job.required_skills?.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      <span className="text-[10px] text-slate-400 font-bold mr-1">Required:</span>
                      {job.required_skills.map(s => (
                        <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  )}

                  {job.matches.length === 0 ? (
                    <p className="text-sm text-slate-400 italic py-2">No available candidates match yet. Add more to the pool.</p>
                  ) : (
                    <div className="space-y-2">
                      {job.matches.map(m => {
                        const key = `${job.job_id}__${m.candidate_id}`;
                        const scored = scoreResults[key];
                        return (
                          <div key={m.candidate_id} className="border border-slate-100 rounded-lg px-3 py-2.5 bg-slate-50">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-display font-bold text-sm">{m.name}</span>
                                  <span className={`text-sm font-bold ${m.match_score >= 70 ? 'text-emerald-600' : m.match_score >= 50 ? 'text-amber-600' : 'text-slate-500'}`}>
                                    {m.match_score}% match
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{m.current_role} · {m.years_experience}y exp</p>
                                {m.matching_skills?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {m.matching_skills.map(s => (
                                      <span key={s} className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">{s}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="shrink-0 flex items-center gap-2">
                                {!scored && (
                                  <button
                                    onClick={() => scoreCandidate(job.job_id, m.candidate_id)}
                                    disabled={scoring === key}
                                    className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 bg-purple-600 text-white rounded-sm font-bold disabled:opacity-40"
                                    data-testid={`score-btn-${m.candidate_id}`}
                                  >
                                    {scoring === key ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />}
                                    AI Score
                                  </button>
                                )}
                                <button
                                  onClick={() => nav(`/app/hr/recruitment/pool`)}
                                  className="text-[11px] text-[#3b4872] hover:underline"
                                >
                                  View
                                </button>
                              </div>
                            </div>

                            {scored && (
                              <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="text-center">
                                  <p className={`font-bold text-xl ${GRADE_COLOR[scored.grade] || ''}`}>{scored.overall_score}</p>
                                  <p className="text-[9px] text-slate-400">Overall</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-sm text-slate-700">{scored.skills_match}%</p>
                                  <p className="text-[9px] text-slate-400">Skills</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-sm text-slate-700">{scored.experience_match}%</p>
                                  <p className="text-[9px] text-slate-400">Experience</p>
                                </div>
                                <div className="text-center">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${REC_BG[scored.recommendation] || 'bg-slate-100 text-slate-600'}`}>
                                    {scored.recommendation}
                                  </span>
                                  <p className="text-[9px] text-slate-400 mt-0.5">Rec.</p>
                                </div>
                                {scored.positives?.length > 0 && (
                                  <div className="col-span-2 md:col-span-4 mt-1">
                                    <p className="text-[10px] text-slate-400 font-bold mb-1">AI Positives</p>
                                    <ul className="space-y-0.5">
                                      {scored.positives.map((p, i) => <li key={i} className="text-[11px] text-emerald-700">✓ {p}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {scored.concerns?.length > 0 && (
                                  <div className="col-span-2 md:col-span-4">
                                    <p className="text-[10px] text-slate-400 font-bold mb-1">Concerns</p>
                                    <ul className="space-y-0.5">
                                      {scored.concerns.map((c, i) => <li key={i} className="text-[11px] text-amber-700">⚠ {c}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {scored.interview_questions?.length > 0 && (
                                  <div className="col-span-2 md:col-span-4">
                                    <p className="text-[10px] text-slate-400 font-bold mb-1">Suggested Interview Questions</p>
                                    <ol className="space-y-0.5 list-decimal list-inside">
                                      {scored.interview_questions.map((q, i) => <li key={i} className="text-[11px] text-slate-600">{q}</li>)}
                                    </ol>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
